/**
 * Shared upgrade flow for Stripe checkout (US132b / US140 / US141).
 *
 * Encapsulates the "user clicks Upgrade → backend creates Stripe Checkout
 * Session → browser redirects to hosted Stripe page" sequence. The hook
 * is intentionally UI-agnostic so it can drive buttons in the
 * ``PaywallModal``, ``UpgradeBanner``, ``Settings`` page, or anywhere
 * else we need to deep-link the user into Stripe.
 *
 * Design notes:
 *
 * - The hook does **not** mount Stripe.js — Stripe Checkout is a fully
 *   hosted page. We just redirect.
 * - ``isStripeConfigured()`` reports the build-time
 *   ``VITE_STRIPE_PUBLISHABLE_KEY`` value. It's used for two things:
 *   (a) driving ``paymentUnavailable`` / ``ready`` so the UI can
 *   render a "Coming Soon" placeholder in builds that don't have a
 *   publishable key, and (b) implicitly gating the click target
 *   (callers wire their button through ``ready``).
 * - The click handlers (``startUpgrade`` / ``startReactivation`` / ``startManageBilling``)
 *   deliberately do **not** short-circuit on ``isStripeConfigured``.
 *   They always call the backend; a 503 from
 *   ``/billing/checkout-session`` flips ``paymentUnavailable`` to
 *   true and surfaces a "coming soon" toast. This means a build with
 *   the publishable key but missing backend secrets still gives the
 *   user a clear error path rather than a silent no-op.
 * - Network / 503 errors are surfaced via the ``error`` string so callers
 *   can show a toast. The hook never throws — call sites don't need
 *   try/catch.
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createBillingPortalSession,
  createCheckoutSession,
  isStripeConfigured,
} from "../services/billing";
import { track } from "../services/analytics";
import { getErrorMessage } from "../api";
import { useAuth } from "../contexts/AuthContext";

export type Currency = "gbp" | "usd";
export type Interval = "month" | "year";

export interface UseUpgradeFlowResult {
  /** True when both the publishable key is set and no upgrade is in flight. */
  ready: boolean;
  /** True while the backend call is in flight. */
  loading: boolean;
  /** Last error message (cleared on next call). */
  error: string | null;
  /**
   * Trigger the upgrade flow: create a Stripe Checkout Session and
   * redirect the browser to it. Safe to call multiple times; the
   * in-flight guard prevents double-redirects.
   *
   * US268: when called without a price point (no currency/interval),
   * the hook signals that the caller should show a price-selection
   * modal instead of immediately redirecting.
   */
  startUpgrade: (opts?: {
    currency?: Currency;
    interval?: Interval;
  }) => Promise<void>;
  /**
   * Trigger the reactivation flow (US132c): open the Stripe Billing
   * Portal for the current user. Stripe will let the user re-subscribe
   * to the same plan without going through checkout again. Falls back
   * to a fresh checkout session (via the price-selection modal) if the
   * user does not yet have a Stripe customer record (returned as 404 by
   * the backend).
   */
  startReactivation: () => Promise<void>;
  /**
   * Open the Stripe Billing Portal for the current user (US140).
   * Falls back to a fresh checkout session (via the price-selection
   * modal) if the user has no Stripe customer record (404). Surfaces
   * 503 as a toast-friendly error.
   */
  startManageBilling: () => Promise<void>;
  /** True while the reactivation / portal call is in flight. */
  reactivating: boolean;
  /** True while the manage-billing call is in flight. */
  managingBilling: boolean;
  /** True when Stripe is not configured (frontend env or backend 503). */
  paymentUnavailable: boolean;
  /**
   * US268: true when the caller should render the price-selection
   * modal because the user started an upgrade without a pre-selected
   * price point.
   */
  showPriceSelectionModal: boolean;
  /** US268: closes the price-selection modal without upgrading. */
  cancelPriceSelection: () => void;
  /** US268: confirms a price point and continues to Stripe checkout. */
  confirmPriceSelection: (opts: {
    currency: Currency;
    interval: Interval;
  }) => Promise<void>;
}

export function useUpgradeFlow(): UseUpgradeFlowResult {
  const [loading, setLoading] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [managingBilling, setManagingBilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [showPriceSelectionModal, setShowPriceSelectionModal] = useState(false);
  const { isGuest } = useAuth();
  const navigate = useNavigate();

  const ready = isStripeConfigured() && !loading && !unavailable;

  /**
   * Guest accounts must register (with email validation) before they
   * can access Stripe checkout. Redirect to /register and return true
   * so callers can short-circuit.
   */
  const redirectGuestToRegister = useCallback((): boolean => {
    if (isGuest) {
      track("guest_upgrade_blocked", { reason: "guest_account" });
      navigate("/register");
      return true;
    }
    return false;
  }, [isGuest, navigate]);

  const startUpgrade = useCallback(
    async (opts?: { currency?: Currency; interval?: Interval }) => {
      if (loading) return;
      if (redirectGuestToRegister()) return;
      setError(null);
      track("upgrade_clicked", {
        currency: opts?.currency,
        interval: opts?.interval,
      });

      // US268: if no price point was pre-selected, open the modal
      // instead of falling back to the legacy single price id.
      if (!opts?.currency || !opts?.interval) {
        setShowPriceSelectionModal(true);
        return;
      }

      setLoading(true);
      try {
        const session = await createCheckoutSession({
          currency: opts?.currency,
          interval: opts?.interval,
        });
        window.location.assign(session.url);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 503) {
          setUnavailable(true);
          setError("Billing is not configured on this server.");
        } else {
          setError(
            getErrorMessage(err, "Unable to start checkout. Please try again."),
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [loading, redirectGuestToRegister],
  );

  const cancelPriceSelection = useCallback(() => {
    setShowPriceSelectionModal(false);
  }, []);

  const confirmPriceSelection = useCallback(
    async (opts: { currency: Currency; interval: Interval }) => {
      setShowPriceSelectionModal(false);
      await startUpgrade(opts);
    },
    [startUpgrade],
  );

  const startReactivation = useCallback(async () => {
    if (reactivating) return;
    if (redirectGuestToRegister()) return;
    setReactivating(true);
    setError(null);
    try {
      try {
        const { url } = await createBillingPortalSession();
        window.location.assign(url);
        return;
      } catch (portalErr: unknown) {
        const status = (portalErr as { response?: { status?: number } })
          ?.response?.status;
        // 404 means the user has no Stripe customer record yet — fall
        // back to a fresh checkout so the reactivation always succeeds
        // (the user just types in card details again).
        if (status !== 404) {
          throw portalErr;
        }
      }
      // US268: open price-selection modal instead of legacy single-price fallback.
      setShowPriceSelectionModal(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 503) {
        setUnavailable(true);
        setError("Billing is not configured on this server.");
      } else {
        setError(
          getErrorMessage(
            err,
            "Unable to start reactivation. Please try again.",
          ),
        );
      }
    } finally {
      setReactivating(false);
    }
  }, [reactivating, redirectGuestToRegister]);

  const startManageBilling = useCallback(async () => {
    if (managingBilling) return;
    if (redirectGuestToRegister()) return;
    setManagingBilling(true);
    setError(null);
    try {
      try {
        const { url } = await createBillingPortalSession();
        window.location.assign(url);
        return;
      } catch (portalErr: unknown) {
        const status = (portalErr as { response?: { status?: number } })
          ?.response?.status;
        // 404 means no Stripe customer yet — fall back to checkout.
        if (status !== 404) {
          throw portalErr;
        }
      }
      // US268: open price-selection modal instead of legacy single-price fallback.
      setShowPriceSelectionModal(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 503) {
        setUnavailable(true);
        setError("Billing is not configured on this server.");
      } else {
        setError(
          getErrorMessage(
            err,
            "Unable to open billing portal. Please try again.",
          ),
        );
      }
    } finally {
      setManagingBilling(false);
    }
  }, [managingBilling, redirectGuestToRegister]);

  return {
    ready,
    loading,
    error,
    startUpgrade,
    startReactivation,
    startManageBilling,
    reactivating,
    managingBilling,
    paymentUnavailable: unavailable || !isStripeConfigured(),
    showPriceSelectionModal,
    cancelPriceSelection,
    confirmPriceSelection,
  };
}
